import React from "react";
import PropTypes from "prop-types";
import { Save, History, CheckCircle2, Loader2 } from "lucide-react";
import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", dot: "bg-amber-500" },
  { value: "in_progress", label: "In Progress", dot: "bg-blue-500" },
  { value: "awaiting_parts", label: "Awaiting Parts", dot: "bg-purple-500" },
  { value: "completed", label: "Completed", dot: "bg-emerald-500" },
  { value: "cancelled", label: "Cancelled", dot: "bg-rose-500" },
];

const StatusUpdateModal = ({
  show,
  onHide,
  statusFormData,
  setStatusFormData,
  handleSaveStatus,
  updateStatusMutation,
  handleViewStatus,
  repairJobs = [],
  selectedRepairJob,
}) => {
  const currentStatus = statusFormData?.status || "";
  const currentNotes = statusFormData?.notes || "";

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          <span>Update Status</span>
          {selectedRepairJob && (
            <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              #{selectedRepairJob.repair_number || selectedRepairJob.id}
              {selectedRepairJob.item_description
                ? ` - ${selectedRepairJob.item_description}`
                : ""}
            </span>
          )}
        </div>
      }
      icon={CheckCircle2}
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-between w-full">
          {handleViewStatus ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs text-primary hover:text-primary hover:bg-primary/5"
              onClick={() => {
                onHide();
                const jobToView =
                  repairJobs.find((j) => j.id === selectedRepairJob?.id) ||
                  selectedRepairJob;
                handleViewStatus(jobToView);
              }}
            >
              <History size={14} /> View History
            </Button>
          ) : (
            <div />
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onHide}
              disabled={updateStatusMutation?.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              onClick={handleSaveStatus}
              disabled={!currentStatus || updateStatusMutation?.isPending}
            >
              {updateStatusMutation?.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save size={14} /> Update Status
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 px-1 py-1">
        <div className="space-y-2">
          <Label className="font-medium">
            Status <span className="text-destructive">*</span>
          </Label>
          <Select
            value={currentStatus}
            onValueChange={(value) =>
              setStatusFormData((prev) => ({
                ...prev,
                status: value,
              }))
            }
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select a status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                    <span>{opt.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Notes</Label>
            <span className="text-[10px] text-muted-foreground">Optional</span>
          </div>
          <Textarea
            placeholder="Add an internal note about this status update..."
            value={currentNotes}
            onChange={(e) =>
              setStatusFormData((prev) => ({
                ...prev,
                notes: e.target.value,
              }))
            }
            maxLength={1000}
            rows={4}
            className="text-sm resize-none"
          />
          <p className="text-[10px] text-muted-foreground text-right italic">
            {currentNotes.length}/1000 characters
          </p>
        </div>
      </div>
    </Modal>
  );
};

StatusUpdateModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  statusFormData: PropTypes.shape({
    status: PropTypes.string,
    notes: PropTypes.string,
  }).isRequired,
  setStatusFormData: PropTypes.func.isRequired,
  handleSaveStatus: PropTypes.func.isRequired,
  updateStatusMutation: PropTypes.shape({
    isPending: PropTypes.bool,
  }),
  handleViewStatus: PropTypes.func,
  repairJobs: PropTypes.array,
  selectedRepairJob: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    repair_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item_description: PropTypes.string,
  }),
};

export default StatusUpdateModal;
